import {Injectable} from '@angular/core';
import {NavController} from '@ionic/angular';
import {Params} from '@angular/router';
import { ChargePointDto } from '../../domain/infra/chargePoint/chargePoint';
import { MediaFamilyType } from '../../domain/enum/mediaFamilyType';
import { MediaSummaryDto } from '../../domain/media/mediaSummary';
import { OTPSession } from '../../domain/otp/otp';
import { PlugTypeChoice } from '../../domain/infra/plug/plugTypeChoice';
import { ReservationDuration } from '../../domain/reservation/reservation';
import {from, Observable} from 'rxjs';
import {last} from 'lodash-es';
import {v4 as uuidv4} from 'uuid';

export const CONTEXT_KEY_PARAM = 'contextId';

@Injectable({
    providedIn: 'root'
})
export class NewChargeContextService {

    private readonly contexts: NewChargeContext[] = [];

    constructor(private readonly navController: NavController) {
    }

    startNewCharge(redirectUrl: string, chargeAreaMarkerId?: string, isReservable?: boolean): Observable<boolean> {
        const newChargeContext = this.createNewEmptyChargeContext(chargeAreaMarkerId, isReservable);
        return this.goToChargeContextView(redirectUrl, newChargeContext);
    }

    startNewChargeWithNewChargeContext(newChargeContext: NewChargeContext, redirectUrl: string): Observable<boolean> {
        return this.goToChargeContextView(redirectUrl, newChargeContext);
    }

    private goToChargeContextView(redirectUrl: string, newChargeContext: NewChargeContext) {
        return from(this.navController.navigateForward([redirectUrl], {
            queryParams: newChargeContext.toQueryParam()
        }));
    }

    /**
     * Find newChargeContext by id or retrieve the latest (useful when URL is used without `contextId` query param)
     *
     * @param id Context identifier (optional)
     */
    getNewChargeContext(id?: string): NewChargeContext | undefined {
        if (id) {
            let newChargeContext = this.contexts.find(context => context.id === id);

            if (!newChargeContext) {
                newChargeContext = Object.assign(new NewChargeContext(), JSON.parse(localStorage.getItem(id)));
                if (!!newChargeContext) {
                    this.contexts.push(newChargeContext);
                }
            }

            return newChargeContext;
        } else {
            return last(this.contexts);
        }
    }

    removeNewChargeContext(id: string): void {
        const index = this.contexts.findIndex(context => context.id === id);
        if (index !== -1) {
            this.contexts.splice(index, 1);
        }
    }

    getOrCreateNewChargeContext(id?: string): NewChargeContext {
        if (id) {
            let newChargeContext = this.contexts.find(context => context.id === id);
            if (newChargeContext) {
                return newChargeContext;
            }
        }

        return this.createNewEmptyChargeContext();
    }

    public createNewEmptyChargeContext(chargeAreaMarkerId?: string, isReservable?: boolean): NewChargeContext {
        const newChargeContext: NewChargeContext = new NewChargeContext(chargeAreaMarkerId, isReservable);

        this.contexts.push(newChargeContext);

        return newChargeContext;
    }
}

export class NewChargeContext {
    id: string;
    chargeAreaMarkerId?: string;
    stationId?: string;
    plugTypeChoice?: PlugTypeChoice;
    chargePoint?: ChargePointDto;
    reservation?: ReservationDuration;
    mediaSummary?: MediaSummaryDto;
    otpSession?: OTPSession;

    goingToStep: boolean;
    isReservable: boolean;

    isRetryOnOtherStation: boolean = false;

    constructor(chargeAreaMarkerId?: string, isReservable?: boolean) {
        this.id = uuidv4();
        this.chargeAreaMarkerId = chargeAreaMarkerId;
        this.isReservable = isReservable;
    }

    isOTP(): boolean {
        if (!this.mediaSummary || !this.mediaSummary.familyType) {
            return undefined;
        }

        return MediaFamilyType.OTP_CLIENT === this.mediaSummary.familyType || this.isOtpCb();
    }

    isOtpCb(): boolean {
        if (!this.mediaSummary || !this.mediaSummary.familyType) {
            return undefined;
        }

        return MediaFamilyType.OTP_CB === this.mediaSummary.familyType
            || MediaFamilyType.OTP_SMS === this.mediaSummary.familyType;
    }

    canFetchOtpPrice(): boolean {
        return !!this.chargePoint?.stationAlias;
    }

    hasReservation(): boolean {
        return this.isReservable && !!this.reservation;
    }

    toQueryParam(): Params {
        const params = {};

        params[CONTEXT_KEY_PARAM] = this.id;

        return params;
    }
}
